import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X,
  Loader2,
  AlertCircle,
  Plus,
  Minus,
  Settings,
  Calendar,
  User,
  FileText,
} from 'lucide-react';
import { getApiBase, getAuthHeaders } from '../../utils/api';

const formatAmount = (cents) => {
  if (!cents && cents !== 0) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getChangeIcon = (changeType) => {
  switch (changeType) {
    case 'purchase':
      return <Plus className="w-4 h-4 text-green-500" />;
    case 'removal':
      return <Minus className="w-4 h-4 text-red-500" />;
    case 'adjustment':
      return <Settings className="w-4 h-4 text-blue-500" />;
    default:
      return <FileText className="w-4 h-4 text-gray-500" />;
  }
};

const getChangeLabel = (changeType) => {
  switch (changeType) {
    case 'purchase':
      return 'Purchased';
    case 'removal':
      return 'Scheduled Removal';
    case 'adjustment':
      return 'Adjustment';
    default:
      return changeType;
  }
};

const SeatHistoryModal = ({ organizationId, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${getApiBase()}/organizations/${organizationId}/seats/history`,
          { headers: getAuthHeaders() }
        );
        setHistory(response.data.history);
        setTotal(response.data.total);
        setError('');
      } catch (err) {
        console.error('Error fetching seat history:', err);
        setError(err.response?.data?.detail || 'Failed to load history');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [organizationId]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Seat Purchase History
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg p-4">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">No seat changes recorded yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="mt-1">{getChangeIcon(item.change_type)}</div>
                      <div>
                        <div className="font-medium text-gray-900">
                          {Math.abs(item.quantity)} {item.seat_type === 'executive' ? 'Executive' : 'EA'} seat
                          {Math.abs(item.quantity) !== 1 ? 's' : ''}{' '}
                          {item.quantity > 0 ? 'added' : 'removed'}
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          <span className="inline-flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {item.purchased_by_name}
                          </span>
                          <span className="mx-2">|</span>
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(item.created_at)}
                          </span>
                        </div>
                        {item.scheduled_for && !item.applied_at && (
                          <div className="text-sm text-orange-600 mt-1">
                            Scheduled for: {formatDate(item.scheduled_for)}
                          </div>
                        )}
                        {item.applied_at && (
                          <div className="text-sm text-green-600 mt-1">
                            Applied: {formatDate(item.applied_at)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs rounded-full ${
                          item.change_type === 'purchase'
                            ? 'bg-green-100 text-green-700'
                            : item.change_type === 'removal'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {getChangeLabel(item.change_type)}
                      </span>
                    </div>
                  </div>

                  {/* Pricing details */}
                  {item.change_type === 'purchase' && item.prorated_amount > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Unit price:</span>
                          <div className="font-medium">{formatAmount(item.unit_price)}/mo</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Monthly change:</span>
                          <div className="font-medium text-green-600">
                            +{formatAmount(item.total_monthly_change)}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">Charged:</span>
                          <div className="font-medium">{formatAmount(item.prorated_amount)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Invoice link */}
                  {item.invoice_id && (
                    <div className="mt-2 text-xs text-gray-500">
                      Invoice: {item.invoice_id.slice(0, 8)}...
                    </div>
                  )}

                  {/* Notes */}
                  {item.notes && (
                    <div className="mt-2 text-sm text-gray-600 italic">
                      Note: {item.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <span className="text-sm text-gray-500">
            {total} total record{total !== 1 ? 's' : ''}
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SeatHistoryModal;
